// Coach Check-In Audit — "staff recorded on these candidate sessions" (A′).
//
// A bounded legacy-audit aid: it puts the Dash staffing the candidate search ALREADY returned in front
// of the auditor, across every candidate -- including those hidden by the Form-level filter. It adds
// no query, ranks nobody and infers nothing: no name similarity, no nickname logic, no "likely match".
// Whether a typed Form name refers to one of these people is the auditor's judgment.
//
// The only name marker is the EXISTING exact Form-name evidence (`matches_form_name` on the class
// roster rows the API already returns). A person gets ★ only if a roster row for that same employee,
// on one of their candidate classes, already carries it.

export const SUMMARY_INITIAL_LIMIT = 12;

const byTimeThenName = (a, b) => String(a.time || '').localeCompare(String(b.time || ''))
  || String(a.class_name || '').localeCompare(String(b.class_name || ''));

/**
 * @param {object}   args
 * @param {Array}    args.candidates      every candidate the search returned (visible or not)
 * @param {Array}    args.sessionStaff    session_staff from the API; undefined/null = older API
 * @param {Array}    args.coaches         roster rows (team_dim_id, employee_id, matches_form_name)
 * @param {Set}      args.visibleEventIds event_ids currently shown under the Form-level filter
 * @returns {null | { people, hiddenCandidatesWithStaff, unstaffedCandidates, candidateCount }}
 *          null when the API supplied no session staffing, so the caller hides the summary.
 */
export function summarizeSessionStaff({ candidates = [], sessionStaff, coaches = [], visibleEventIds }) {
  if (!Array.isArray(sessionStaff)) return null;

  const candByEvent = new Map(candidates.map((c) => [c.event_id, c]));
  const isHidden = (eventId) => Boolean(visibleEventIds) && !visibleEventIds.has(eventId);

  // Exact Form-name evidence, keyed by (team, employee), exactly as the roster reports it.
  const exactOnTeam = new Set(
    coaches.filter((r) => r.matches_form_name === 1 || r.matches_form_name === true)
      .map((r) => `${r.team_dim_id}|${r.employee_id}`),
  );

  const people = new Map();
  const seen = new Set();
  const staffedEvents = new Set();
  sessionStaff.forEach((s) => {
    const cand = candByEvent.get(s.event_id);
    if (!cand) return;                                  // staffing for a session not in this search
    const key = `${s.event_id}|${s.employee_id}`;
    if (seen.has(key)) return;                          // one row per (event_id, employee_id)
    seen.add(key);
    staffedEvents.add(s.event_id);

    const id = String(s.employee_id);
    if (!people.has(id)) {
      people.set(id, { employee_id: id, name: s.coach_display_name || id, classes: [], exactFormName: false });
    }
    const p = people.get(id);
    p.classes.push({
      event_id: cand.event_id,
      class_name: cand.class_name,
      level: cand.canonical_level_name || null,
      time: cand.event_start_time || null,
      hidden: isHidden(cand.event_id),
    });
    if (exactOnTeam.has(`${cand.team_dim_id}|${s.employee_id}`)) p.exactFormName = true;
  });

  const list = [...people.values()]
    .map((p) => ({ ...p, classes: p.classes.sort(byTimeThenName), allHidden: p.classes.every((c) => c.hidden) }))
    // Alphabetical only. Nothing here is a ranking.
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      || a.employee_id.localeCompare(b.employee_id));

  return {
    people: list,
    hiddenCandidatesWithStaff: candidates.filter((c) => isHidden(c.event_id) && staffedEvents.has(c.event_id)).length,
    unstaffedCandidates: candidates.filter((c) => !staffedEvents.has(c.event_id)).length,
    candidateCount: candidates.length,
  };
}
